import Tesseract from 'tesseract.js';
import { showError, showLog } from 'src/utils/debug-log';

/**
 * OCR Detection Function
 * @param {File | string} imageInput - File object, a local file path, or an image URL.
 * @returns {Promise<object>} Extracted structured data from OCR.
 */
export const ocrDetection = async (imageInput, currentSeconds = 0) => {
  return new Promise((resolve, reject) => {
    try {
      let imagePath;

      // Check if input is a URL (http/https)
      const isURL = typeof imageInput === 'string' && /^https?:\/\//.test(imageInput);

      if (isURL) {
        // Fetch image from URL and process as Blob
        fetch(imageInput)
          .then((response) => {
            if (!response.ok) throw new Error('Failed to fetch image from URL.');
            return response.blob(); // Convert to Blob
          })
          .then((blob) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              imagePath = e.target.result; // Base64 string
              processOCR(imagePath, currentSeconds).then(resolve).catch(reject);
            };
            reader.onerror = () => {
              reject(new Error('Failed to process fetched image.'));
            };
            reader.readAsDataURL(blob);
          })
          .catch((error) => reject(error));
      } else if (typeof imageInput === 'string') {
        // Local file path
        imagePath = imageInput;
        processOCR(imagePath, currentSeconds).then(resolve).catch(reject);
      } else if (imageInput instanceof File) {
        // File selected from input
        const reader = new FileReader();
        reader.onload = (e) => {
          imagePath = e.target.result; // Base64 string
          processOCR(imagePath, currentSeconds).then(resolve).catch(reject);
        };
        reader.onerror = () => {
          reject(new Error('Failed to read file.'));
        };
        reader.readAsDataURL(imageInput);
      } else {
        resolve({}); // Return empty object for invalid inputs
      }
    } catch (error) {
      resolve({}); // Graceful fallback on errors
    }
  });
};

/**
 * Internal OCR Processing
 * @param {string} imagePath - Image path or Base64 URL
 * @returns {Promise<object>} Extracted structured data.
 */
const processOCR = (imagePath, currentSeconds) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = imagePath;

    img.onload = () => {
      try {
        if(img.height < img.width){
          // Portrait only please
          reject(new Error('[Portrait] Chỉ hỗ trợ hình đứng'));
          return;
        }

        // Canvas for cropping
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        let width = 560;
        let height = 280;
        let startX = 0;
        let startY = 0;
        
        let percent = img.height / 1600;
        if(percent !== 1){
          percent = Math.ceil(percent * 100) / 100; // To achieve rounding to the nearest 0.01 increment
        }
        width = Math.round(width * percent);
        height = Math.round(height * percent);
        startX = Math.round(startX * percent);
        startY = Math.round(startY * percent);

        const cropWidth = Math.min(width, img.width);
        const cropHeight = Math.min(height, img.height);
        canvas.width = cropWidth;
        canvas.height = cropHeight;

        // Draw the cropped area (bottom-left corner)
        ctx.drawImage(
          img,
          startX, // Start x
          img.height - (cropHeight + startY),
          cropWidth, // Crop width
          cropHeight, // Crop height
          0, // Place at x=0 on canvas
          0, // Place at y=0 on canvas
          canvas.width, // Scale to canvas width
          canvas.height // Scale to canvas height
        );

        // Convert canvas to Data URL (base64 string) for OCR
        const croppedImageData = canvas.toDataURL();

        // Using only for develop
        // croppedPreview(croppedImageData);

        // Perform OCR
        processRecognize(currentSeconds, croppedImageData, resolve, reject);
      } catch (err) {
        showError(err);
        reject(new Error('Không lấy được dữ liệu của hình'));
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load the image. Check the file path or upload.'));
    };
  });
};

const processRecognize = async (currentSeconds, croppedImageData, resolve, reject) => {
  // Perform OCR
  Tesseract.recognize(croppedImageData, 
    'eng',//'eng+vie'
    {
      //langPath: 'path_to_tesseract_languages',  // Optional if you have custom language files
      //tessedit_char_whitelist: '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',  // Optional: Add character whitelist
      tessedit_char_whitelist: '0123456789:.,_/- ',
      preserve_interword_spaces: true,  // Optional: To preserve spaces
      tessedit_pageseg_mode: Tesseract.PSM.AUTO, //Tesseract.PSM.[SINGLE_BLOCK or AUTO or SINGLE_LINE]  // Optional: Specify page segmentation mode
      // logger: (info) => {
      //   if (info.status === 'recognizing text') {
      //     showLog(`OCR Progress: ${Math.round(info.progress * 100)}%`);
      //   }
      // },
    }
  ).then(({ data: { text } }) => {
    // showLog('OCR Result:', text);
    if(0 === currentSeconds){
      currentSeconds = Math.floor(Date.now() / 1000);
    }
    const parsedData = parseImageData(currentSeconds, text);
    resolve(parsedData);
  }).catch((err) => reject(err));
};

const parseImageData = async (seconds, text) => {
  // Split text into lines, clean whitespace
  const lines = text.split('\n').map((line) => line.trim()).filter((line) => line);

  const result = {
    timestamp: null,  // Seconds since epoch
    datetime: null,   // Formatted datetime string
    location: null,   // { lat, lng }
  };
  const date = new Date(seconds * 1000);

  // Define Regex Patterns
  const dateTimeRegex = /(\d{1,2})[\s:-](\d{1,2})\s+(\d{1,2})\/(\d{1,2})\/(\d{4})/; // HH:MM DD/MM/YYYY with time separators
  const locationRegex = /(-?\d+\.\d+)[,;]\s*(-?\d+\.\d+)/;  // Latitude, Longitude with comma or semicolon separator

  // Helper function to clean lines
  const cleanLine = (line) => 
    line
      .replace(/\]/g, '7')  // Replace "]" with "7"
      .replace(/\|/g, '1')   // Replace "|" with "1"
      .replace(/[^0-9:/\s-.,;]/g, '')  // Remove any characters not in the list
      .trim();  // Remove leading/trailing spaces

  // Process lines one by one
  for (let i = 0; i < lines.length; i++) {
    let line = cleanLine(lines[i]);
    line = correctOCRText(line);

    //showLog('Line:', lines[i], line);

    // Match Date-Time
    const dateTimeMatch = line.match(dateTimeRegex);
    if (dateTimeMatch) {
      // Extract hour and minute
      const [ , hour, minute, day, month, year ] = dateTimeMatch;

      // Check if hour and minute exist, then set day, month, and year with defaults if missing
      const validHour = (/^\d{1}$/.test(hour)) ? date.getHours() : hour;
      const validMinute =  (/^\d{1}$/.test(minute)) ?  date.getMinutes() : minute;
      const validDay = day ? day : date.getDay();
      const validMonth = month ? month : date.getMonth();
      const validYear = year ? year : date.getFullYear();

      // Create the timestamp and formatted datetime string
      result.timestamp = Math.floor(
        new Date(validYear, validMonth - 1, validDay, validHour, validMinute).getTime() / 1000
      );
      result.datetime = `${validHour}:${validMinute} ${validDay}/${validMonth}/${validYear}`;

      // showLog('Matched Date-Time:', result.timestamp, '=', result.datetime);
      continue; // Continue to the next line
    }
    
    // Match Location (latitude, longitude)
    const locationMatch = line.match(locationRegex);
    if (locationMatch) {
      // Extract latitude and longitude
      const [ , latStr, lngStr ] = locationMatch;
      const lat = parseFloat(latStr);
      const lng = parseFloat(lngStr);
      // Validate latitude and longitude
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        result.location = `${lat},${lng}`;
      }
      continue; // Continue to next line
    }
  }

  // Return the structured result
  return result;
};

const correctOCRText = (text) => {
  // Replace common OCR mistakes after detection
  return text
    // Fix misinterpretations of numbers with slashes (e.g., "1/", "9/")
    .replace(/\b0\//g, '07')  // "0/" as "07"
    .replace(/\b1\//g, '17')  // "1/" as "17"
    .replace(/\b2\//g, '27')  // "2/" as "27"
    .replace(/\b3\//g, '37')  // "3/" as "37"
    .replace(/\b4\//g, '47')  // "4/" as "47"
    .replace(/\b5\//g, '57')  // "5/" as "57"

    // Fix misinterpretations of vertical bar ("|") instead of numbers
    // .replace(/\b0\|/g, '01')  // "0|" as "01"
    // .replace(/\b1\|/g, '11')  // "1|" as "11"
    // .replace(/\b2\|/g, '12')  // "2|" as "12"
    // .replace(/\b3\|/g, '13')  // "3|" as "13"
    // .replace(/\b4\|/g, '14')  // "4|" as "14"
    // .replace(/\b5\|/g, '15')  // "5|" as "15"
    // .replace(/\b6\|/g, '16')  // "6|" as "16"
    // .replace(/\b7\|/g, '17')  // "7|" as "17"
    // .replace(/\b8\|/g, '18')  // "8|" as "18"
    // .replace(/\b9\|/g, '19')  // "9|" as "19"
};

/**
 * Convert yyyymmddHHmm or yyyymmddHHmmss format to Date and extract hour and minute.
 * @param {string} dateTimeString - Input in yyyymmddHHmm (12 chars) or yyyymmddHHmmss (14 chars) format.
 * @returns {object} { hour: number, minute: number } - Extracted hour and minute.
 */
export const getSecondsByString = (dateTimeString) => {
  // Remove all non-numeric characters
  const cleanedString = dateTimeString.replace(/\D/g, '');

  // Extract the first 12 or 14 characters
  const dateTimeSubString = cleanedString.slice(0, 14); // Take the first 14 characters
  const validLength = dateTimeSubString.length === 12 || dateTimeSubString.length === 14;

  // Check if the extracted string is numeric and of valid length (12 or 14)
  if (!validLength || !/^\d+$/.test(dateTimeSubString)) {
    // Invalid input. Only numbers are allowed and length must be 12 or 14.
    return 0;
  }

  // Extract year, month, day, hour, minute, and second (if present)
  const year = parseInt(dateTimeSubString.substring(0, 4), 10);
  const month = parseInt(dateTimeSubString.substring(4, 6), 10) - 1; // Month is zero-based (0-11)
  const day = parseInt(dateTimeSubString.substring(6, 8), 10);
  const hour = parseInt(dateTimeSubString.substring(8, 10), 10);
  const minute = parseInt(dateTimeSubString.substring(10, 12), 10);
  const second = dateTimeSubString.length === 14 ? parseInt(dateTimeSubString.substring(12, 14), 10) : 0; // Default to 0 for seconds

  // Validate the month, day, hour, and minute ranges
  if (month < 0 || month > 11 || day < 1 || day > 31 || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return 0; // Return empty object if validation fails
  }

  // Create the Date object from extracted values
  const date = new Date(year, month, day, hour, minute, second);
  // Convert the Date object to seconds
  const seconds = Math.floor(date.getTime() / 1000);

  return seconds;
};

// Using for debug
function croppedPreview(croppedImageData){
  const img = new Image();
  img.src = croppedImageData;  // This is your base64 image data

  // Create a container box for the image
  const box = document.createElement('div');
  box.style.width = '300px';  // Set the width of the box
  box.style.height = '200px'; // Set the height of the box
  box.style.border = '2px solid #000'; // Optional: Add a border around the box for visibility
  box.style.display = 'flex';
  box.style.justifyContent = 'center';  // Center the image horizontally
  box.style.alignItems = 'center';      // Center the image vertically
  box.style.overflow = 'hidden';        // Ensure the image fits inside the box

  // Set image size within the box
  img.style.maxWidth = '100%';   // Ensure the image fits inside the box horizontally
  img.style.maxHeight = '100%';  // Ensure the image fits inside the box vertically

  // Append the image to the box
  box.appendChild(img);

  // Create a wrapper to center the box on the screen
  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed'; // Make it fixed on the screen
  wrapper.style.top = '50%';        // Center vertically
  wrapper.style.left = '50%';       // Center horizontally
  wrapper.style.transform = 'translate(-50%, -50%)'; // Offset by half the width and height
  wrapper.style.display = 'flex';   // Use flexbox to center the box

  // Append the box to the wrapper
  wrapper.appendChild(box);

  // Append the wrapper to the body of the page
  document.body.appendChild(wrapper);
}
